import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import type { Ref, ComponentPublicInstance } from 'vue'

type ContainerRefItem = HTMLElement | ComponentPublicInstance | null
type ContainerRef = Ref<ContainerRefItem | ContainerRefItem[]>

export function useKeyboardNavigation(
    containerRef: ContainerRef,
    options: {
        selector: string;
        useFirstChildOnly: boolean;
        childSelector: string;
        onNavigate?: ((direction: string, event: KeyboardEvent) => void) | null
    } = {
        selector: '[tabindex]:not([tabindex="-1"]), a, button, input, select, textarea',
        useFirstChildOnly: false,
        childSelector: '',
        onNavigate: null as ((direction: string, event: KeyboardEvent) => void) | null
    }
) {
    const focusableElements = ref<HTMLElement[]>([])

    // Check if an element is inside another element
    const isChildOf = (child: HTMLElement, parent: HTMLElement): boolean => {
        if (child === parent) return true
        let element: HTMLElement | null = child
        while (element) {
            if (element === parent) return true
            element = element.parentElement
        }
        return false
    }

    // Find the container that contains the focused element
    const findContainerWithFocus = (): HTMLElement | null => {
        const activeElement = document.activeElement as HTMLElement
        if (!activeElement) return null

        for (const element of focusableElements.value) {
            if (isChildOf(activeElement, element)) {
                return element
            }
        }

        return null
    }

    // Handle keyboard navigation
    const handleKeyDown = (event: KeyboardEvent) => {
        // Skip if not an arrow key
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
            return
        }

        // Refresh list if empty
        if (focusableElements.value.length === 0) {
            updateFocusableElements()
            if (focusableElements.value.length === 0) return
        }

        // Find which container has focus (either directly or through a child)
        const containerWithFocus = findContainerWithFocus()
        if (!containerWithFocus) return

        // Get index of container with focus
        const currentIndex = focusableElements.value.indexOf(containerWithFocus)
        if (currentIndex === -1) return

        const direction = event.key.replace('Arrow', '').toLowerCase()
        let handled = false

        // Handle horizontal navigation with left/right
        if (['left', 'right'].includes(direction)) {
            let targetIndex = currentIndex

            if (direction === 'right' && currentIndex < focusableElements.value.length - 1) {
                targetIndex = currentIndex + 1
                handled = true
            } else if (direction === 'left' && currentIndex > 0) {
                targetIndex = currentIndex - 1
                handled = true
            }

            if (handled) {
                // Focus the target element
                focusableElements.value[targetIndex]!.focus()
                event.preventDefault()
                event.stopPropagation()
            }
        }

        // Call custom navigate handler if provided
        if (options.onNavigate) {
            options.onNavigate(direction, event)
        }
    }

    // Updates the list of focusable elements and attaches event listeners
    const updateFocusableElements = async () => {
        await nextTick()

        // Remove previous event listeners
        focusableElements.value.forEach(element => {
            element.removeEventListener('keydown', handleKeyDown)
        })

        // Reset the list
        focusableElements.value = []

        if (!containerRef?.value) return

        // Get the container element(s)
        let containers: HTMLElement[] = []

        if (Array.isArray(containerRef.value)) {
            // Handle array of refs (like slide refs) to turn into HTML Elements
            containers = (containerRef.value as ContainerRefItem[])
                .filter((item): item is NonNullable<ContainerRefItem> => Boolean(item))
                .map((item): HTMLElement => (item as ComponentPublicInstance).$el ?? item)
                .filter(Boolean) as HTMLElement[]
        } else {
            // Handle single container ref
            const rawValue = containerRef.value
            const container: HTMLElement | null = rawValue instanceof HTMLElement
                ? rawValue
                : (rawValue as ComponentPublicInstance)?.$el ?? null
            if (container) {
                if (options.useFirstChildOnly && options.childSelector) {
                    // Find child containers (e.g., slides)
                    containers = Array.from(
                        container.querySelectorAll(options.childSelector)
                    ) as HTMLElement[]
                } else {
                    containers = [container]
                }
            }
        }

        // Find focusable elements in each container
        const elements: HTMLElement[] = []

        containers.forEach(container => {
            if (options.useFirstChildOnly) {
                // Find just the first focusable element in this container
                const firstFocusable = container.querySelector(options.selector) as HTMLElement
                if (firstFocusable) {
                    elements.push(firstFocusable)
                }
            } else {
                // Find all focusable elements in this container
                const focusables = Array.from(
                    container.querySelectorAll(options.selector)
                ) as HTMLElement[]
                elements.push(...focusables)
            }
        })

        focusableElements.value = elements

        // Attach event listeners to each focusable element
        focusableElements.value.forEach(element => {
            element.addEventListener('keydown', handleKeyDown)
        })
    }

    // Setup
    onMounted(() => {
        updateFocusableElements()
    })

    // Cleanup
    onBeforeUnmount(() => {
        // Remove all event listeners
        focusableElements.value.forEach(element => {
            element.removeEventListener('keydown', handleKeyDown)
        })
    })

    // Watch for container changes
    if (containerRef) {
        watch(() => containerRef.value, updateFocusableElements, { deep: true })
    }

    return {
        focusableElements,
        updateFocusableElements
    }
}
